import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import DictableTextarea from './DictableTextarea';

const vuoto = () => ({
  ospite_id: '', data_ora: new Date().toISOString().slice(0, 16), luogo: 'Camera', luogo_dettaglio: '',
  attivita: 'Deambulazione autonoma', testimoniata: false, testimone: '', ausili_presenti: false,
  ausilio_tipo: '', calzature: 'Calzature adeguate', contenzione_presente: false, contenzione_tipo: '',
  dinamica: '', gravita: 'nessuna', conseguenze: '', esami: [], provvedimenti: '', follow_up: '',
});

export default function CadutaForm({ open, onOpenChange, ospiti, ospiteId, onSaved }) {
  const [defs, setDefs] = useState(null);
  const [form, setForm] = useState(vuoto());
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { api.get('/cadute/definizioni').then(r => setDefs(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (open) setForm({ ...vuoto(), ospite_id: ospiteId || '' }); }, [open, ospiteId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleEsame = (e) => set('esami', form.esami.includes(e) ? form.esami.filter(x => x !== e) : [...form.esami, e]);

  const salva = async () => {
    if (!form.ospite_id) return toast.error('Seleziona l\'ospite');
    if (!form.dinamica.trim()) return toast.error('Descrivi la dinamica della caduta');
    setSalvando(true);
    try {
      await api.post('/cadute', form);
      toast.success('Caduta registrata. Ricordati di rivalutare la scala di Conley.');
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setSalvando(false);
  };

  if (!defs) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Scheda caduta</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!ospiteId && (
            <div>
              <Label className="text-xs">Ospite *</Label>
              <Select value={form.ospite_id} onValueChange={v => set('ospite_id', v)}>
                <SelectTrigger data-testid="caduta-ospite"><SelectValue placeholder="Seleziona ospite" /></SelectTrigger>
                <SelectContent>{(ospiti || []).map(o => <SelectItem key={o.id} value={o.id}>{o.cognome} {o.nome}{o.stanza ? ` - cam. ${o.stanza}` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Data e ora *</Label>
              <Input type="datetime-local" value={form.data_ora} onChange={e => set('data_ora', e.target.value)} data-testid="caduta-data" /></div>
            <div><Label className="text-xs">Luogo *</Label>
              <Select value={form.luogo} onValueChange={v => set('luogo', v)}>
                <SelectTrigger data-testid="caduta-luogo"><SelectValue /></SelectTrigger>
                <SelectContent>{defs.luoghi.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Attivita al momento della caduta *</Label>
              <Select value={form.attivita} onValueChange={v => set('attivita', v)}>
                <SelectTrigger data-testid="caduta-attivita"><SelectValue /></SelectTrigger>
                <SelectContent>{defs.attivita.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Calzature</Label>
              <Select value={form.calzature} onValueChange={v => set('calzature', v)}>
                <SelectTrigger data-testid="caduta-calzature"><SelectValue /></SelectTrigger>
                <SelectContent>{defs.calzature.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50 rounded-lg p-3">
            <label className="flex items-center justify-between gap-2 text-xs text-slate-600">
              Caduta testimoniata
              <Switch checked={form.testimoniata} onCheckedChange={v => set('testimoniata', v)} data-testid="caduta-testimoniata" />
            </label>
            <label className="flex items-center justify-between gap-2 text-xs text-slate-600">
              Ausili presenti
              <Switch checked={form.ausili_presenti} onCheckedChange={v => set('ausili_presenti', v)} data-testid="caduta-ausili" />
            </label>
            <label className="flex items-center justify-between gap-2 text-xs text-slate-600">
              Contenzione in atto
              <Switch checked={form.contenzione_presente} onCheckedChange={v => set('contenzione_presente', v)} data-testid="caduta-contenzione" />
            </label>
            {form.testimoniata && <Input className="h-8 text-xs" placeholder="Chi ha assistito" value={form.testimone} onChange={e => set('testimone', e.target.value)} />}
            {form.ausili_presenti && <Input className="h-8 text-xs" placeholder="Deambulatore, bastone..." value={form.ausilio_tipo} onChange={e => set('ausilio_tipo', e.target.value)} />}
            {form.contenzione_presente && <Input className="h-8 text-xs" placeholder="Spondine, cintura..." value={form.contenzione_tipo} onChange={e => set('contenzione_tipo', e.target.value)} />}
          </div>
          <div><Label className="text-xs">Dinamica *</Label>
            <DictableTextarea rows={3} value={form.dinamica} onChange={e => set('dinamica', e.target.value)} data-testid="caduta-dinamica" /></div>
          <div>
            <Label className="text-xs">Conseguenze - grado di gravita *</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {defs.gravita.map(g => (
                <button key={g.valore} type="button" onClick={() => set('gravita', g.valore)}
                  className={`px-3 py-1.5 text-xs rounded-md border font-medium transition-colors ${form.gravita === g.valore
                    ? g.livello === 'rosso' ? 'bg-red-600 text-white border-red-600'
                      : g.livello === 'giallo' ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-slate-500 border-slate-200'}`}
                  data-testid={`caduta-gravita-${g.valore}`}>{g.label}</button>
              ))}
            </div>
          </div>
          <div><Label className="text-xs">Descrizione delle conseguenze</Label>
            <DictableTextarea rows={2} value={form.conseguenze} onChange={e => set('conseguenze', e.target.value)} /></div>
          <div>
            <Label className="text-xs">Esami e valutazioni eseguite</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {defs.esami.map(e => (
                <button key={e} type="button" onClick={() => toggleEsame(e)}
                  className={`px-2.5 py-1.5 text-[11px] rounded-md border transition-colors ${form.esami.includes(e) ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200'}`}
                  data-testid={`caduta-esame-${e.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{e}</button>
              ))}
            </div>
          </div>
          <div><Label className="text-xs">Provvedimenti adottati</Label>
            <DictableTextarea rows={2} value={form.provvedimenti} onChange={e => set('provvedimenti', e.target.value)} data-testid="caduta-provvedimenti" /></div>
          <div><Label className="text-xs">Follow-up</Label>
            <Input value={form.follow_up} onChange={e => set('follow_up', e.target.value)} placeholder="Controlli programmati, rivalutazione..." /></div>
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            Dopo il salvataggio la scala di Conley risultera da rivalutare: compilala nel tab Scale Valutative dell'ospite.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button onClick={salva} disabled={salvando} className="bg-brand hover:bg-brand-dark text-white" data-testid="salva-caduta">
              {salvando ? 'Salvataggio...' : 'Salva scheda caduta'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
